import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { carryTo } from "@/lib/carryParams";
import { cn } from "@/lib/cn";
import { useT } from "@/lib/i18n";
import type { BessUnitSnapshot } from "@/data/types";

/**
 * One-line fleet-verdict strip for the BESS page (F18). A plain-language
 * read of the fleet derived ONLY from per-unit facts the adapter can
 * actually source — online count, faults, lowest SOC — NOT from
 * bess_aggregate.fault_count (unreliable in live). It doubles as a drill
 * link to the worst unit, so it complements (rather than restates) the
 * Mini KPI strip's numeric online-count / SOC-range.
 *
 * `units` is expected pre-sorted worst-first by the caller, so the worst
 * unit named here agrees with the first tile in the grid.
 */
export function BessFleetVerdict({ units, total }: { units: BessUnitSnapshot[]; total: number }) {
  const t = useT();

  const onlineUnits = units.filter((u) => u.online);
  const offline = units.filter((u) => !u.online);
  const faulted = onlineUnits.filter((u) => u.fault_count > 0);
  // Lowest-SOC online unit (units arrive sorted, so reduce keeps the first
  // on ties → stable with the grid order).
  const worstOnline = onlineUnits.reduce<BessUnitSnapshot | undefined>(
    (lo, u) => (lo === undefined || u.soc_pct < lo.soc_pct ? u : lo),
    undefined,
  );

  // Single-unit guard: no "fleet"/"worst-first" wording for a lone unit.
  if (total <= 1) {
    const u = units[0];
    if (!u) return null;
    const single = !u.online
      ? { tone: "degraded" as const, text: t("analyze.bess.verdict.singleOffline", { name: u.name }) }
      : u.fault_count > 0
        ? { tone: "degraded" as const, text: t("analyze.bess.verdict.singleFault", { name: u.name }) }
        : { tone: "ok" as const, text: t("analyze.bess.verdict.singleOk", { soc: u.soc_pct.toFixed(0) }) };
    return <Strip tone={single.tone} aria={t("analyze.bess.verdict.aria")}>{single.text}</Strip>;
  }

  const tone: "ok" | "degraded" = offline.length || faulted.length ? "degraded" : "ok";

  // Worst unit for the drill link: first offline, else first faulted, else
  // the lowest-SOC online unit.
  const worstUnit = offline[0] ?? faulted[0] ?? worstOnline;
  const worstLink = worstUnit ? (
    <Link to={carryTo(`/bess/${worstUnit.id}`)} className="text-accent hover:underline">
      {worstUnit.name}
    </Link>
  ) : null;

  if (tone === "ok") {
    // Healthy — interpolate the worst (lowest-SOC) unit name as a drill
    // Link. Split out {name} via a sentinel so the surrounding word order
    // is preserved in any locale.
    const soc = worstOnline ? worstOnline.soc_pct.toFixed(0) : "0";
    const SENTINEL = " NAME ";
    const [before, after = ""] = t("analyze.bess.verdict.healthy", {
      online: onlineUnits.length,
      total,
      name: SENTINEL,
      soc,
    }).split(SENTINEL);
    return (
      <Strip tone="ok" aria={t("analyze.bess.verdict.aria")}>
        <span className="flex flex-wrap items-center gap-1">
          <span>{before}</span>
          {worstLink}
          <span>{after}</span>
        </span>
      </Strip>
    );
  }

  // Degraded — a compact plain-language clause list. One offline clause
  // (name the first, "+N more") and/or one faulted clause.
  const clauses: ReactNode[] = [];
  clauses.push(
    <span key="online">{t("analyze.bess.verdict.online", { online: onlineUnits.length, total })}</span>,
  );
  if (offline.length > 0) {
    clauses.push(
      <span key="offline">
        {offline.length > 1
          ? t("analyze.bess.verdict.offlineMore", { name: offline[0].name, n: offline.length - 1 })
          : t("analyze.bess.verdict.offlineOne", { name: offline[0].name })}
      </span>,
    );
  }
  if (faulted.length > 0) {
    clauses.push(
      <span key="faulted">
        {faulted.length > 1
          ? t("analyze.bess.verdict.faultMore", { name: faulted[0].name, n: faulted.length - 1 })
          : t("analyze.bess.verdict.faultOne", { name: faulted[0].name })}
      </span>,
    );
  }

  return (
    <Strip tone="degraded" aria={t("analyze.bess.verdict.aria")}>
      {clauses.map((c, i) => (
        <span key={i} className="flex items-center gap-2">
          {i > 0 && <span className="text-text-faint">·</span>}
          {c}
        </span>
      ))}
      {worstLink && (
        <span className="flex items-center gap-2">
          <span className="text-text-faint">·</span>
          {worstLink}
        </span>
      )}
    </Strip>
  );
}

function Strip({
  tone,
  aria,
  children,
}: {
  tone: "ok" | "degraded";
  aria: string;
  children: ReactNode;
}) {
  const Icon = tone === "ok" ? CheckCircle2 : AlertTriangle;
  return (
    <div role="region" aria-label={aria} className="card flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm">
      <span className={cn("pip", tone === "ok" ? "bg-ok" : "bg-warn")} />
      <Icon size={16} className={tone === "ok" ? "text-ok" : "text-warn"} aria-hidden />
      <span className="flex flex-wrap items-center gap-2 text-text">{children}</span>
    </div>
  );
}
